import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@medikei/ui';
import { AuthLayout, AuthHeader, FormDivider, FormFooter } from '@/components/auth';
import { authApi, httpClient, AuthApiError } from '@/api/http-client';
import { useAuth } from '@/hooks';
import {
  ShieldCheck,
  Smartphone,
  Clock,
  ArrowLeft,
  XCircle,
} from 'lucide-react';
import { cn } from '@/utils/cn';

interface MFAPageState {
  pendingSessionId: string;
  userId?: string;
  email?: string;
}

export function MFAPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backupCodeMode, setBackupCodeMode] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const state = location.state as MFAPageState;
  const { pendingSessionId, userId } = state || {};

  useEffect(() => {
    if (!pendingSessionId) {
      navigate('/login');
      return;
    }
    inputRef.current?.focus();
  }, [pendingSessionId, navigate]);

  useEffect(() => {
    if (lockoutUntil) {
      const remaining = lockoutUntil.getTime() - Date.now();
      if (remaining <= 0) {
        setLockoutUntil(null);
        setAttempts(0);
      }
    }
  }, [lockoutUntil]);

  const isLocked = useMemo(() => {
    return lockoutUntil !== null && lockoutUntil.getTime() > Date.now();
  }, [lockoutUntil]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) return;

    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.verify2FA(
        pendingSessionId,
        userId || '',
        code,
        backupCodeMode
      );

      httpClient.saveAccessToken(response.accessToken);
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const error = err as Error;
      setAttempts((prev) => prev + 1);

      if (error instanceof AuthApiError) {
        if (error.code === 'INVALID_2FA_CODE') {
          if (attempts >= 4) {
            setLockoutUntil(new Date(Date.now() + 5 * 60 * 1000));
            setError('Muitas tentativas. Conta bloqueada por 5 minutos.');
          } else {
            setError(`Código inválido. ${5 - attempts} tentativas restantes.`);
          }
        } else if (error.code === 'INVALID_BACKUP_CODE') {
          setError('Código de backup inválido.');
        } else if (error.code === 'RATE_LIMIT') {
          setError('Muitas tentativas. Tente novamente mais tarde.');
        } else {
          setError(error.message || 'Erro na verificação');
        }
      } else {
        setError('Erro na verificação. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (backupCodeMode) {
      setCode(value);
    } else {
      setCode(value.slice(0, 6));
    }
  };

  const formatTime = (date: Date) => {
    const minutes = Math.floor((date.getTime() - Date.now()) / 60000);
    const seconds = Math.floor(((date.getTime() - Date.now()) % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <AuthLayout>
      <div className="flex flex-col items-center mb-6">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <ShieldCheck size={28} className="text-primary" />
        </div>
      </div>

      <AuthHeader
        title="Verificação em Duas Etapas"
        description="Insira o código de 6 dígitos do seu aplicativo autenticador"
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {isLocked && lockoutUntil && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
            <Clock size={18} className="text-yellow-500 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              Conta bloqueada. Tente novamente em{' '}
              <strong>{formatTime(lockoutUntil)}</strong>
            </p>
          </div>
        )}

        <div className="space-y-2">
          <label
            htmlFor="code"
            className="block text-sm font-medium text-gray-700 text-center"
          >
            {backupCodeMode ? 'Código de Backup' : 'Código do Aplicativo'}
          </label>
          <input
            ref={inputRef}
            id="code"
            type="text"
            inputMode="numeric"
            pattern={backupCodeMode ? '[0-9]*' : '[0-9]{6}'}
            value={code}
            onChange={handleCodeChange}
            className={cn(
              'w-full h-12 text-center text-xl tracking-widest rounded-lg border-2 bg-white font-mono',
              'focus:outline-none focus:ring-2 transition-all',
              error
                ? 'border-red-400 focus:ring-red-200'
                : 'border-gray-300 hover:border-gray-400 focus:border-primary focus:ring-primary/20'
            )}
            placeholder={backupCodeMode ? '000000' : '000 000'}
            disabled={isLoading || isLocked}
            autoComplete={backupCodeMode ? 'off' : 'one-time-code'}
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading || !code || isLocked}
          loading={isLoading}
        >
          {isLoading ? 'Verificando...' : 'Verificar'}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setBackupCodeMode(!backupCodeMode);
              setCode('');
              setError(null);
            }}
            className="text-sm font-medium text-primary hover:text-primary/80 flex items-center gap-1 mx-auto"
          >
            <Smartphone size={16} />
            {backupCodeMode ? 'Usar código do app' : 'Usar código de backup'}
          </button>
        </div>

        <FormDivider />

        <FormFooter>
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Voltar para login
          </Link>
        </FormFooter>
      </form>

      <p className="text-xs text-gray-500 text-center mt-4">
        Problemas para acessar?{' '}
        <Link to="/support" className="text-primary hover:underline">
          Entre em contato com o suporte
        </Link>
      </p>
    </AuthLayout>
  );
}